import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  InputAdornment,
  Grid,
  IconButton,
  Checkbox,
  FormGroup,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { Region, RegionalConfig } from '@/services/settingsService';

interface RegionalVATConfigProps {
  vatConfigs: RegionalConfig<{
    enabled: boolean;
    percentage: number;
    applyTo: ('bookingFee' | 'rentalCost' | 'deliveryFee')[];
  }>[];
  onChange: (configs: RegionalConfig<{
    enabled: boolean;
    percentage: number;
    applyTo: ('bookingFee' | 'rentalCost' | 'deliveryFee')[];
  }>[]) => void;
  errors?: Record<string, string>;
  clearError?: (field: string) => void;
}

const AVAILABLE_REGIONS: Region[] = ['Lagos', 'Nairobi', 'Accra', 'All'];

export const RegionalVATConfig: React.FC<RegionalVATConfigProps> = ({
  vatConfigs,
  onChange,
  errors = {},
  clearError,
}) => {
  const handleAddConfig = () => {
    onChange([
      ...vatConfigs,
      {
        regions: [],
        value: {
          enabled: true,
          percentage: 7.5,
          applyTo: ['bookingFee', 'rentalCost'],
        },
      },
    ]);
  };

  const handleRemoveConfig = (index: number) => {
    const newConfigs = vatConfigs.filter((_, i) => i !== index);
    onChange(newConfigs);
  };

  const handleRegionToggle = (configIndex: number, region: Region) => {
    const newConfigs = [...vatConfigs];
    const config = newConfigs[configIndex];

    if (config.regions.includes(region)) {
      config.regions = config.regions.filter((r) => r !== region);
    } else {
      config.regions = [...config.regions, region];
    }

    onChange(newConfigs);
    clearError?.(`vat_${configIndex}_regions`);
  };

  const handleValueChange = (
    configIndex: number,
    field: 'enabled' | 'percentage',
    value: boolean | number
  ) => {
    const newConfigs = [...vatConfigs];
    newConfigs[configIndex].value = {
      ...newConfigs[configIndex].value,
      [field]: value,
    };
    onChange(newConfigs);
    clearError?.(`vat_${configIndex}_${field}`);
  };

  const handleApplyToToggle = (
    configIndex: number,
    item: 'bookingFee' | 'rentalCost' | 'deliveryFee'
  ) => {
    const newConfigs = [...vatConfigs];
    const applyTo = newConfigs[configIndex].value.applyTo;

    if (applyTo.includes(item)) {
      newConfigs[configIndex].value.applyTo = applyTo.filter((i) => i !== item);
    } else {
      newConfigs[configIndex].value.applyTo = [...applyTo, item];
    }

    onChange(newConfigs);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              VAT Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure region-specific Value Added Tax settings
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddConfig}
            size="small"
          >
            Add Region
          </Button>
        </Box>

        {vatConfigs.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No VAT configurations. Click "Add Region" to create one.
            </Typography>
          </Box>
        )}

        {vatConfigs.map((config, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Configuration #{index + 1}
                </Typography>

                {/* Region Selection */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Regions
                  </Typography>
                  <FormGroup row>
                    {AVAILABLE_REGIONS.map((region) => (
                      <FormControlLabel
                        key={region}
                        control={
                          <Checkbox
                            checked={config.regions.includes(region)}
                            onChange={() => handleRegionToggle(index, region)}
                          />
                        }
                        label={region}
                      />
                    ))}
                  </FormGroup>
                  {errors[`vat_${index}_regions`] && (
                    <Typography variant="caption" color="error">
                      {errors[`vat_${index}_regions`]}
                    </Typography>
                  )}
                </Box>

                {/* Enable Switch */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.value.enabled}
                      onChange={(e) => handleValueChange(index, 'enabled', e.target.checked)}
                    />
                  }
                  label="Enable VAT for these regions"
                  sx={{ mb: 2 }}
                />

                {config.value.enabled && (
                  <>
                    {/* VAT Percentage */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        VAT Percentage
                      </Typography>
                      <TextField
                        type="number"
                        value={config.value.percentage}
                        onChange={(e) => handleValueChange(index, 'percentage', Number(e.target.value))}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        error={!!errors[`vat_${index}_percentage`]}
                        helperText={errors[`vat_${index}_percentage`] || 'Standard rates: Nigeria 7.5%, Kenya 16%, Ghana 12.5%'}
                        fullWidth
                        size="small"
                      />
                    </Box>

                    {/* Apply To */}
                    <Box>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Apply VAT To
                      </Typography>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.value.applyTo.includes('rentalCost')}
                              onChange={() => handleApplyToToggle(index, 'rentalCost')}
                            />
                          }
                          label="Rental Cost"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.value.applyTo.includes('bookingFee')}
                              onChange={() => handleApplyToToggle(index, 'bookingFee')}
                            />
                          }
                          label="Booking Fee"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.value.applyTo.includes('deliveryFee')}
                              onChange={() => handleApplyToToggle(index, 'deliveryFee')}
                            />
                          }
                          label="Delivery Fee"
                        />
                      </FormGroup>
                    </Box>
                  </>
                )}
              </Box>

              <IconButton
                onClick={() => handleRemoveConfig(index)}
                color="error"
                size="small"
                sx={{ ml: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            {/* Display selected regions as chips */}
            {config.regions.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                {config.regions.map((region) => (
                  <Chip key={region} label={region} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            )}
          </Paper>
        ))}
      </CardContent>
    </Card>
  );
};
